import { useEffect, useRef } from 'react';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function PlayButtonFlame({ active, buttonR = 34 }) {
  const canvasRef = useRef(null);
  const activeRef = useRef(active);

  useEffect(() => { activeRef.current = active; }, [active]);

  useEffect(() => {
    if (prefersReducedMotion()) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const s = buttonR / 34; // scale factor from the reference 68px button
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const W = Math.round(180 * s);
    const H = Math.round(220 * s);
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
    canvas.style.width = W + 'px';
    canvas.style.height = H + 'px';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const cx = W / 2;
    const cy = Math.round(140 * s);

    const isMobile = window.innerWidth < 640;
    const sizeScale = s;
    const MAX_FLAMES = Math.round((isMobile ? 60 : 95) * (0.55 + 0.45 * sizeScale));
    const MAX_SPARKS = Math.round((isMobile ? 18 : 30) * (0.55 + 0.45 * sizeScale));
    const mobileScale = isMobile ? 0.7 : 1;
    const rateScale = 0.55 + 0.45 * sizeScale;

    const particles = [];

    const spawnFlame = () => {
      const g = () => (Math.random() + Math.random() + Math.random()) / 3 - 0.5;
      const angle = Math.PI / 2 + g() * Math.PI * 1.4;
      const rOff = buttonR + (1 + Math.random() * 5) * s;
      const x = cx + Math.cos(angle) * rOff;
      const y = cy + Math.sin(angle) * rOff;

      const outwardX = Math.cos(angle) * 0.15 * s;
      const speed = (0.85 + Math.random() * 0.9) * s;

      particles.push({
        type: 'flame',
        x, y,
        vx: outwardX + (Math.random() - 0.5) * 0.22 * s,
        vy: -speed,
        r0: (10 + Math.random() * 10) * s,
        life: 780 + Math.random() * 620,
        age: 0,
        wob: Math.random() * Math.PI * 2,
        wobF: 0.0035 + Math.random() * 0.0045,
        wobA: (0.35 + Math.random() * 0.35) * s,
        hueSpin: (Math.random() - 0.5) * 8,
      });
    };

    const spawnSpark = () => {
      const g = () => (Math.random() + Math.random()) / 2 - 0.5;
      const angle = Math.PI / 2 + g() * Math.PI * 1.1;
      const rOff = buttonR + Math.random() * 5 * s;
      particles.push({
        type: 'spark',
        x: cx + Math.cos(angle) * rOff,
        y: cy + Math.sin(angle) * rOff,
        vx: (Math.random() - 0.5) * 0.4 * s,
        vy: -(1.4 + Math.random() * 1.6) * s,
        r0: (1 + Math.random() * 1.3) * s,
        life: 950 + Math.random() * 850,
        age: 0,
        wob: Math.random() * Math.PI * 2,
        wobF: 0.008 + Math.random() * 0.007,
      });
    };

    let raf = 0;
    let running = true;
    let last = performance.now();
    let flameAcc = 0;
    let sparkAcc = 0;
    let coalPhase = 0;

    const tick = (t) => {
      if (!running) return;
      const dt = Math.min(48, t - last);
      last = t;
      coalPhase += dt * 0.003;

      const isActive = activeRef.current;
      const intensity = isActive ? 1 : 0.35;

      ctx.clearRect(0, 0, W, H);
      ctx.globalCompositeOperation = 'lighter';

      const flick = 0.85 + 0.15 * Math.sin(coalPhase) + 0.08 * Math.sin(coalPhase * 2.3);
      const coalY = cy + buttonR - 4 * s;
      for (const [rr, a1, a2] of [
        [56, 0.55, 0.30],
        [34, 0.75, 0.35],
        [20, 0.95, 0.55],
      ]) {
        const r = rr * s;
        const grad = ctx.createRadialGradient(cx, coalY, 0, cx, coalY, r);
        grad.addColorStop(0,   `rgba(255, 220, 130, ${a1 * intensity * flick})`);
        grad.addColorStop(0.5, `rgba(255, 120, 40, ${a2 * intensity * flick})`);
        grad.addColorStop(1,   `rgba(255, 40, 10, 0)`);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(cx, coalY, r, 0, Math.PI * 2);
        ctx.fill();
      }

      const flameRate = (isActive ? 0.62 : 0.22) * mobileScale * rateScale;
      const sparkRate = (isActive ? 0.11 : 0.038) * mobileScale * rateScale;
      flameAcc += dt * flameRate;
      sparkAcc += dt * sparkRate;

      let flameCount = 0;
      let sparkCount = 0;
      for (const p of particles) (p.type === 'flame' ? flameCount++ : sparkCount++);

      while (flameAcc >= 1) {
        if (flameCount < MAX_FLAMES) { spawnFlame(); flameCount++; }
        flameAcc -= 1;
      }
      while (sparkAcc >= 1) {
        if (sparkCount < MAX_SPARKS) { spawnSpark(); sparkCount++; }
        sparkAcc -= 1;
      }

      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.age += dt;
        if (p.age > p.life) { particles.splice(i, 1); continue; }
        p.wob += dt * p.wobF;

        if (p.type === 'flame') {
          p.vy *= 1 - 0.00035 * dt;
          p.x += p.vx + Math.sin(p.wob) * p.wobA;
          p.y += p.vy;

          const t01 = p.age / p.life;
          const growPhase = Math.min(1, t01 / 0.14);
          const shrink = 1 - Math.max(0, (t01 - 0.14) / 0.86) * 0.55;
          const r = p.r0 * (0.55 + 0.45 * growPhase) * shrink;

          let hue, sat, light, alpha;
          if (t01 < 0.12) {
            hue = 55 + p.hueSpin; sat = 100; light = 84;
            alpha = growPhase * 0.9 * intensity;
          } else if (t01 < 0.35) {
            const k = (t01 - 0.12) / 0.23;
            hue = 55 - k * 22 + p.hueSpin;
            sat = 100;
            light = 84 - k * 22;
            alpha = 0.78 * intensity;
          } else if (t01 < 0.72) {
            const k = (t01 - 0.35) / 0.37;
            hue = 33 - k * 22 + p.hueSpin;
            sat = 100 - k * 15;
            light = 62 - k * 22;
            alpha = (0.72 - k * 0.32) * intensity;
          } else {
            const k = (t01 - 0.72) / 0.28;
            hue = 11 - k * 5;
            sat = 85 - k * 45;
            light = 40 - k * 25;
            alpha = (0.32 - k * 0.32) * intensity;
          }
          alpha = Math.max(0, alpha);

          const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r);
          grad.addColorStop(0,   `hsla(${hue}, ${sat}%, ${light}%, ${alpha})`);
          grad.addColorStop(0.55, `hsla(${hue - 12}, ${sat}%, ${Math.max(18, light - 18)}%, ${alpha * 0.5})`);
          grad.addColorStop(1,   `hsla(${Math.max(0, hue - 22)}, ${Math.max(30, sat - 30)}%, ${Math.max(8, light - 30)}%, 0)`);
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
          ctx.fill();
        } else {
          p.x += p.vx + Math.sin(p.wob) * 0.28 * s;
          p.y += p.vy;
          p.vy *= 1 - 0.00028 * dt;

          const t01 = p.age / p.life;
          const flicker = 0.75 + 0.25 * Math.sin(p.wob * 3.7);
          const alpha = (1 - t01) * 0.95 * intensity * flicker;
          const r = p.r0 * (1 - t01 * 0.3);
          const R = r * 4;
          const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, R);
          grad.addColorStop(0,    `hsla(55, 100%, 90%, ${alpha})`);
          grad.addColorStop(0.35, `hsla(30, 100%, 62%, ${alpha * 0.65})`);
          grad.addColorStop(1,    `hsla(12, 100%, 45%, 0)`);
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.arc(p.x, p.y, R, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);

    const handleVisibility = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(raf);
      } else if (!running) {
        running = true;
        last = performance.now();
        raf = requestAnimationFrame(tick);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [buttonR]);

  const s = buttonR / 34;
  const W = Math.round(180 * s);
  const H = Math.round(220 * s);
  const shift = Math.round(30 * s);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        width: `${W}px`,
        height: `${H}px`,
        transform: `translate(-50%, calc(-50% - ${shift}px))`,
        zIndex: 1,
        pointerEvents: 'none',
      }}
    />
  );
}
